import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiX } from 'react-icons/fi';
import { CreateDataRoomInput, DEFAULT_FOLDERS } from '../../types/dataRoom';

// Type cast icons for React 18 compatibility
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

interface CreateDataRoomModalProps {
  onClose: () => void;
  onSubmit: (input: CreateDataRoomInput) => Promise<void>;
}

const CreateDataRoomModal: React.FC<CreateDataRoomModalProps> = ({
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Data room name is required');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create data room');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modal = (
    <div className="su-dataroom-modal-overlay" onClick={handleOverlayClick}>
      <div className="su-dataroom-modal" role="dialog" aria-modal="true" aria-labelledby="create-dataroom-title">
        <div className="su-dataroom-modal-header">
          <div>
            <h2 id="create-dataroom-title" className="su-dataroom-modal-title">Create Data Room</h2>
            <p className="su-dataroom-modal-subtitle">
              Set up a secure space for document sharing
            </p>
          </div>
          <button
            className="su-dataroom-modal-close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="su-dataroom-modal-body">
            {error && (
              <div
                style={{
                  color: '#f87171',
                  fontSize: '14px',
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                }}
              >
                {error}
              </div>
            )}

            <div className="su-dataroom-form-group">
              <label htmlFor="dataroom-name" className="su-dataroom-label">Data Room Name *</label>
              <input
                id="dataroom-name"
                type="text"
                className="su-dataroom-input"
                placeholder="e.g., Series A Due Diligence"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="su-dataroom-form-group">
              <label htmlFor="dataroom-description" className="su-dataroom-label">Description (Optional)</label>
              <textarea
                id="dataroom-description"
                className="su-dataroom-textarea"
                placeholder="Brief description of this data room..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <p className="su-dataroom-helper">
              Your data room will be created with default folders: {DEFAULT_FOLDERS.join(', ')}.
            </p>
          </div>

          <div className="su-dataroom-modal-footer">
            <button
              type="button"
              className="su-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="su-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'CREATE DATA ROOM'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default CreateDataRoomModal;
